import { useState, useCallback } from "react"
import type { Table, Column, Row } from "@tanstack/react-table"
import { formatDateTime } from "@/v2-common/utils/utils.date"
import { downloadBlob } from "@/v2-ui/utils/utils.download"
import config from "@/v2-backoffice-client/config"
import { acquireTokenSilent } from "@/v2-backoffice-client/auth/auth.instance"
import { useTableContext } from "../table.context"
import type { ColumnMeta } from "../table.types"

const SKIPPED_COLUMNS = new Set(["__select", "__actions", "__spacer"])

type ExportColumn = {
  label: string
}

type ExportPayload = {
  fileName: string
  addTimestampToFileName: boolean
  columns: ExportColumn[]
  rows: (string | number | boolean | null)[][]
}

function getExportUrl() {
  return config.graphqlUrl.replace(/\/graphql$/, "/export/excel")
}

function getColumnMeta<TData>(column: Column<TData, unknown>) {
  return column.columnDef.meta as
    | (ColumnMeta<TData> & { exportValue?: ColumnMeta<TData>["exportValue"] })
    | undefined
}

export function extractExportData<TData>(
  table: Table<TData>,
  useSelection: boolean,
) {
  const visibleColumns = table.getVisibleLeafColumns().filter((col) => {
    return !SKIPPED_COLUMNS.has(col.id)
  })

  const columns: ExportColumn[] = visibleColumns.map((col) => {
    const meta = getColumnMeta(col)
    return { label: meta?.label ?? col.id }
  })

  const rows: Row<TData>[] = useSelection
    ? table.getSelectedRowModel().rows
    : table.getRowModel().rows

  const data = rows.map((row) => {
    return visibleColumns.map((col) => {
      const meta = getColumnMeta(col)
      // row.getValue uses the column's accessorFn (i.e. `getValue`) when defined,
      // so the fallback below already inherits `getValue` for free.
      const value = row.getValue(col.id)

      if (meta?.exportValue) {
        return meta.exportValue({
          key: col.id,
          column: col,
          row,
          value,
        })
      }

      if (value === null || value === undefined) return null
      if (typeof value === "boolean") return value
      if (typeof value === "number") return value
      return String(value)
    })
  })

  return { columns, rows: data }
}

/**
 * Posts rows to the backoffice-api /export/excel endpoint and downloads
 * the produced workbook. Shared beyond tables: any feature that hands out
 * a spreadsheet (e.g. the invitation import template) reuses this instead
 * of bundling a client-side xlsx writer.
 */
export async function sendExportRequest(payload: ExportPayload) {
  const url = getExportUrl()
  const { idToken } = await acquireTokenSilent()

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(payload),
  })

  if (!res.ok) {
    throw new Error(`Export failed: ${res.status} ${res.statusText}`)
  }

  const blob = await res.blob()
  const blobUrl = URL.createObjectURL(blob)

  let fileName = payload.fileName
  if (payload.addTimestampToFileName) {
    fileName = `${fileName}-${formatDateTime(new Date())}`
  }

  downloadBlob(blobUrl, `${fileName}.xlsx`)
  URL.revokeObjectURL(blobUrl)
}

export type UseTableExportInput = {
  fileName?: string
  addTimestampToFileName?: boolean
}

export function useTableExport(input: UseTableExportInput) {
  const { fileName, addTimestampToFileName = true } = input
  const { table } = useTableContext()

  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const exportToExcel = useCallback(
    async (useSelection?: boolean) => {
      if (!fileName) return
      setIsExporting(true)
      setError(null)
      try {
        const data = extractExportData(table, useSelection ?? false)
        await sendExportRequest({ fileName, addTimestampToFileName, ...data })
      } catch (error_) {
        const exportError =
          error_ instanceof Error ? error_ : new Error("Export failed")
        setError(exportError)
        throw exportError
      } finally {
        setIsExporting(false)
      }
    },
    [table, addTimestampToFileName, fileName],
  )

  return { exportToExcel, isExporting, error }
}
